import React, { useMemo, useState } from "react";

import {
   createPracticalExamSlot,
   deletePracticalExamSlot,
   updatePracticalExamSlot,
} from "../../api/practicalExamSlotsService";
import {
   PRACTICAL_EXAM_DEFAULT_CAPACITY,
   PRACTICAL_EXAM_MAX_CAPACITY,
   PRACTICAL_EXAM_TIME_OPTIONS,
   normalizePracticalExamHour,
} from "../../constants/practicalExamCalendar";
import ConfirmDeleteButton from "../Common/ConfirmDeleteButton";
import UIToggle from "../Common/UIToggle";
import {
   buildSelectableInstructorOptions,
   filterInstructorOptions,
   INSTRUCTOR_SEARCH_LIMIT,
   normalizeGearboxValue,
} from "./practicalExamInstructorHelpers";

const pad2 = (value) => String(value).padStart(2, "0");

function todayKey() {
   const date = new Date();
   return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
      date.getDate(),
   )}`;
}

function readFirst(...values) {
   for (const value of values) {
      if (value !== "" && value != null) return value;
   }
   return "";
}

function normalizeTime(value) {
   const normalized = normalizePracticalExamHour(value);
   return normalized || PRACTICAL_EXAM_TIME_OPTIONS[0];
}

function formatDateLabel(value) {
   const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
   if (!match) return value || "—";
   return `${match[3]}.${match[2]}.${match[1]}`;
}

function readSlotReservationCount(slot) {
   const raw = slot?.reservations ?? slot?.slotReservations ?? slot?.raw?.reservations;
   if (Array.isArray(raw)) return raw.filter(Boolean).length;
   if (raw && typeof raw === "object") {
      if (Array.isArray(raw.items)) return raw.items.filter(Boolean).length;
      if (Array.isArray(raw.rows)) return raw.rows.filter(Boolean).length;
      if (Array.isArray(raw.data)) return raw.data.filter(Boolean).length;
      const count = Number(raw.count ?? raw.total ?? raw.length);
      return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
   }
   const count = Number(raw);
   return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
}

function initialFormFromProps({ initialValues = {}, slot = null }) {
   return {
      date: readFirst(initialValues.date, slot?.dateKey, todayKey()),
      time: normalizeTime(
         readFirst(initialValues.time, slot?.timeKey, PRACTICAL_EXAM_TIME_OPTIONS[0]),
      ),
      sector: readFirst(initialValues.sector, slot?.sector, "Botanica"),
      gearbox: normalizeGearboxValue(
         readFirst(initialValues.gearbox, slot?.gearbox, "manual"),
      ),
      capacity: String(
         readFirst(
            initialValues.capacity,
            slot?.capacity,
            String(PRACTICAL_EXAM_DEFAULT_CAPACITY),
         ),
      ),
      instructorId: String(
         readFirst(initialValues.instructorId, slot?.instructorId, ""),
      ),
      isActive:
         typeof initialValues.isActive === "boolean"
            ? initialValues.isActive
            : typeof slot?.isActive === "boolean"
              ? slot.isActive
              : true,
   };
}

function buildPayload(form) {
   const capacity = Number(form.capacity);
   if (!Number.isFinite(capacity) || capacity < 1) {
      throw new Error("Capacitatea minimă este 1.");
   }
   if (capacity > PRACTICAL_EXAM_MAX_CAPACITY) {
      throw new Error(`Capacitatea maximă este ${PRACTICAL_EXAM_MAX_CAPACITY}.`);
   }

   return {
      date: form.date,
      time: form.time,
      sector: form.sector.trim(),
      gearbox: form.gearbox,
      capacity,
      instructorId: form.instructorId ? Number(form.instructorId) : "",
      isActive: Boolean(form.isActive),
   };
}

export default function PracticalExamSlotPopup({
   mode = "create",
   slot = null,
   initialValues = {},
   instructorOptions = [],
   onSaved,
   onAlert,
   onClose,
}) {
   const isEdit = mode === "edit" && slot?.id;
   const [form, setForm] = useState(() =>
      initialFormFromProps({ initialValues, slot }),
   );
   const [saving, setSaving] = useState(false);
   const [deleting, setDeleting] = useState(false);
   const [error, setError] = useState("");
   const [view, setView] = useState("form");
   const [qInstructor, setQInstructor] = useState("");
   const busy = saving || deleting;
   const slotReservationCount = useMemo(
      () => readSlotReservationCount(slot),
      [slot],
   );
   const canDeleteSlot = Boolean(isEdit && slotReservationCount === 0);
   const createMode = !isEdit;

   const reportError = (message) => {
      const text = String(message || "A apărut o eroare.").trim();
      if (typeof onAlert === "function") {
         onAlert("error", text);
      } else {
         setError(text);
      }
   };

   const title = isEdit ? "Editează slot" : "Adaugă slot";
   const selectableInstructorOptions = useMemo(
      () =>
         buildSelectableInstructorOptions({
            instructorOptions,
            selectedId: form.instructorId,
            fallbackLabel: slot?.instructorName || "Instructor",
         }),
      [form.instructorId, instructorOptions, slot?.instructorName],
   );
   const hasInstructorOptions = selectableInstructorOptions.length > 0;
   const selectedInstructorOption = useMemo(
      () =>
         selectableInstructorOptions.find(
            (option) => String(option.value) === String(form.instructorId),
         ) || null,
      [form.instructorId, selectableInstructorOptions],
   );
   const instructorSearch = useMemo(
      () => filterInstructorOptions(selectableInstructorOptions, qInstructor),
      [qInstructor, selectableInstructorOptions],
   );
   const filteredInstructorOptions = instructorSearch.items;
   const instructorResultsLimited = instructorSearch.limited;
   const isSingleInstructorMatch = instructorSearch.isSingleMatch;

   const openInstructorSearch = () => {
      if (busy) return;
      setQInstructor("");
      setView("instructorSearch");
   };

   const selectInstructor = (value) => {
      const option = selectableInstructorOptions.find(
         (entry) => String(entry.value) === String(value),
      );
      setForm((prev) => ({
         ...prev,
         instructorId: String(value),
         sector: option?.sector || prev.sector,
         gearbox: option?.gearbox || prev.gearbox,
      }));
      setView("form");
   };

   const handleInstructorSearchKeyDown = (event, value) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      selectInstructor(value);
   };

   const renderInstructorSearch = () => (
      <>
         <div className="createReservationPopup__search-header">
            <input
               type="text"
               className="createReservationPopup__search-input"
               placeholder="Caută instructor după nume sau telefon..."
               value={qInstructor}
               onChange={(event) => setQInstructor(event.target.value)}
               onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
               }}
               autoFocus
               disabled={busy}
            />
            <div className="createReservationPopup__search-header-actions">
               <button
                  type="button"
                  className="createReservationPopup__btn createReservationPopup__btn--normal"
                  onClick={() => setView("form")}
                  disabled={busy}
               >
                  Înapoi
               </button>
            </div>
         </div>

         {filteredInstructorOptions.length ? (
            <ul className="createReservationPopup__search-list">
               {filteredInstructorOptions.map((option) => {
                  const isActive =
                     String(option.value) === String(form.instructorId);

                  return (
                     <li
                        key={option.value}
                        className={[
                           "createReservationPopup__search-item",
                           isActive
                              ? "createReservationPopup__search-item--highlighted"
                              : "",
                           isSingleInstructorMatch
                              ? "createReservationPopup__search-item--full"
                              : "",
                        ]
                           .filter(Boolean)
                           .join(" ")}
                        role="button"
                        tabIndex={0}
                        onClick={() => selectInstructor(option.value)}
                        onKeyDown={(event) =>
                           handleInstructorSearchKeyDown(event, option.value)
                        }
                     >
                        <h3>{option.label}</h3>
                        {option.phone ? <p>{option.phone}</p> : null}
                     </li>
                  );
               })}
            </ul>
         ) : (
            <div className="createReservationPopup__search-empty">
               Nu am găsit instructori.
            </div>
         )}

         {instructorResultsLimited ? (
            <div className="practical-exam-popup__search-limit">
               Sunt afișate primele {INSTRUCTOR_SEARCH_LIMIT} rezultate. Folosește
               căutarea pentru filtrare.
            </div>
         ) : null}
      </>
   );

   const updateField = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
   };

   const handleSubmit = async (event) => {
      event.preventDefault();
      setSaving(true);
      setError("");

      try {
         if (!form.instructorId) {
            throw new Error("Alege instructorul de examen.");
         }
         const payload = buildPayload(form);
         const result = isEdit
            ? await updatePracticalExamSlot(slot.id, payload)
            : await createPracticalExamSlot(payload);

         onSaved?.(result, isEdit ? "Slot actualizat." : "Slot creat.");
         onClose?.();
      } catch (err) {
         reportError(err?.message || "Nu am putut salva slotul.");
      } finally {
         setSaving(false);
      }
   };

   const handleDelete = async () => {
      if (!isEdit) return;
      if (slotReservationCount > 0) {
         reportError(
            "Slotul are rezervări. Mută sau șterge rezervările înainte de ștergerea slotului.",
         );
         return;
      }
      setDeleting(true);
      setError("");

      try {
         const result = await deletePracticalExamSlot(slot.id);
         onSaved?.(result, "Slot șters.");
         onClose?.();
      } catch (err) {
         reportError(err?.message || "Nu am putut șterge slotul.");
      } finally {
         setDeleting(false);
      }
   };

   const createFieldClassName =
      "createReservationPopup__field createReservationPopup__field--grow-1";

   const renderCreateLockedField = (label, value) => (
      <div className={createFieldClassName}>
         <span className="createReservationPopup__field-label">{label}</span>
         <div className="createReservationPopup__field-line">
            <span className="createReservationPopup__field-text">
               {value || "—"}
            </span>
         </div>
      </div>
   );

   const renderCreateInputField = (label, field, inputProps = {}) => (
      <label className={createFieldClassName}>
         <span className="createReservationPopup__field-label">{label}</span>
         <input
            className="createReservationPopup__input"
            value={form[field]}
            onChange={(event) => updateField(field, event.target.value)}
            disabled={busy}
            {...inputProps}
         />
      </label>
   );

   const renderInstructorField = () => (
      <div
         className="createReservationPopup__field createReservationPopup__field--clickable"
         role="button"
         tabIndex={busy ? -1 : 0}
         aria-disabled={busy}
         onClick={() => {
            if (!busy) openInstructorSearch();
         }}
         onKeyDown={(event) => {
            if (busy) return;
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            openInstructorSearch();
         }}
      >
         <span className="createReservationPopup__field-label">Instructor</span>
         <div className="createReservationPopup__field-line">
            <span className="createReservationPopup__field-text">
               {selectedInstructorOption
                  ? selectedInstructorOption.label
                  : "Alege instructor"}
            </span>
         </div>
         <div className="createReservationPopup__field-line">
            <span className="createReservationPopup__field-text">
               {selectedInstructorOption?.phone || "Telefon instructor"}
            </span>
         </div>
      </div>
   );

   const renderCreateGearboxField = () => (
      <div className="createReservationPopup__field createReservationPopup__field--stacked">
         <span className="createReservationPopup__field-label">Cutie</span>
         <div
            className={`practical-exam-popup__radio-wrapper practical-exam-popup__radio-wrapper--${
               form.gearbox === "manual" ? "left" : "right"
            }`}
         >
            <label className="practical-exam-popup__radio-option">
               <input
                  type="radio"
                  name="practical-exam-slot-gearbox"
                  value="manual"
                  checked={form.gearbox === "manual"}
                  onChange={(event) => updateField("gearbox", event.target.value)}
                  disabled={busy}
               />
               <span>Manual</span>
            </label>

            <label className="practical-exam-popup__radio-option">
               <input
                  type="radio"
                  name="practical-exam-slot-gearbox"
                  value="automatic"
                  checked={form.gearbox === "automatic"}
                  onChange={(event) => updateField("gearbox", event.target.value)}
                  disabled={busy}
               />
               <span>Automat</span>
            </label>
         </div>
      </div>
   );

   const renderCreateActiveField = () => (
      <div className="createReservationPopup__field createReservationPopup__field--toggle createReservationPopup__field--full">
         <span className="createReservationPopup__field-label">Slot activ</span>
         <UIToggle
            checked={form.isActive}
            onChange={(value) => updateField("isActive", value)}
            disabled={busy}
            aria-label="Slot activ"
         />
      </div>
   );

   // La creare ora se alege liber (din 30 în 30 de minute); celula din grilă
   // doar o propune.
   const renderCreateTimeField = () => (
      <label className={createFieldClassName}>
         <span className="createReservationPopup__field-label">Ora</span>
         <select
            className="createReservationPopup__input createReservationPopup__select"
            value={form.time}
            onChange={(event) => updateField("time", normalizeTime(event.target.value))}
            disabled={busy}
         >
            {PRACTICAL_EXAM_TIME_OPTIONS.map((time) => (
               <option key={time} value={time}>
                  {time}
               </option>
            ))}
         </select>
      </label>
   );

   const renderCreateForm = () => (
      <>
         <div className="createReservationPopup__group-body">
            <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
               {renderCreateLockedField("Data", formatDateLabel(form.date))}
               {renderCreateTimeField()}
            </div>

            <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
               {renderCreateInputField("Sector", "sector", { required: true })}
               {renderCreateInputField("Capacitate", "capacity", {
                  type: "number",
                  min: "1",
                  max: String(PRACTICAL_EXAM_MAX_CAPACITY),
                  required: true,
               })}
            </div>

            {renderInstructorField()}

            {renderCreateGearboxField()}

            {renderCreateActiveField()}
         </div>

         <div className="createReservationPopup__group-body">
            <div className="createReservationPopup__actions-row createReservationPopup__actions-row--single">
               <button
                  type="submit"
                  className="createReservationPopup__btn createReservationPopup__btn--save createReservationPopup__btn--action"
                  disabled={busy || !hasInstructorOptions}
               >
                  {saving ? "Se salvează..." : "Creează"}
               </button>
            </div>
         </div>
      </>
   );

   const rootClassName = createMode
      ? "createReservationPopup practical-exam-slot-popup practical-exam-slot-popup--create"
      : "createReservationPopup practical-exam-slot-popup practical-exam-slot-popup--edit";
   const headerClassName = "createReservationPopup__header";
   const titleClassName = "createReservationPopup__title";
   const contentClassName = "createReservationPopup__content";

   return (
      <div className={rootClassName}>
         <div className={headerClassName}>
            <h3 className={titleClassName}>{title}</h3>
         </div>

         <form className={contentClassName} onSubmit={handleSubmit}>
            {error ? (
               <p className="instructorProfileUI__error practical-exam-popup__error">
                  {error}
               </p>
            ) : null}

            {!hasInstructorOptions ? (
               <p className="instructorProfileUI__error practical-exam-popup__error">
                  Nu există instructori marcați ca examen.
               </p>
            ) : null}

            {view === "instructorSearch" ? (
               renderInstructorSearch()
            ) : createMode ? (
               renderCreateForm()
            ) : (
               <>
            <div className="createReservationPopup__group-body">
               <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                  {renderCreateLockedField("Data", formatDateLabel(form.date))}
                  {renderCreateLockedField("Ora", form.time)}
               </div>

               <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                  {renderCreateInputField("Sector", "sector", { required: true })}
                  {renderCreateInputField("Capacitate", "capacity", {
                     type: "number",
                     min: "1",
                     max: String(PRACTICAL_EXAM_MAX_CAPACITY),
                     required: true,
                  })}
               </div>

               {renderInstructorField()}

               {renderCreateGearboxField()}

               {renderCreateActiveField()}
            </div>

            <div className="createReservationPopup__group-body">
               <div
                  className={
                     "createReservationPopup__actions-row " +
                     (isEdit
                        ? "practical-exam-popup__actions-row--two"
                        : "practical-exam-popup__actions-row--single")
                  }
               >
                  {isEdit ? (
                     <ConfirmDeleteButton
                        className="practical-exam-popup__delete-confirm"
                        onConfirm={handleDelete}
                        disabled={busy || !canDeleteSlot}
                        label={
                           slotReservationCount > 0 ? "Slot cu rezervări" : "Șterge"
                        }
                        confirmLabel={deleting ? "Se șterge..." : "Da"}
                        cancelLabel="Nu"
                     />
                  ) : null}
                  <button
                     type="submit"
                     className="createReservationPopup__btn createReservationPopup__btn--save createReservationPopup__btn--action"
                     disabled={busy || !hasInstructorOptions}
                  >
                     {saving ? "Se salvează..." : "Salvează"}
                  </button>
               </div>
            </div>
               </>
            )}
         </form>
      </div>
   );
}
